import styled from '@emotion/styled'
import { Calculation } from '../../../pure-js/libs/calculateLogic/CalculateDiscountTypes'
import { Texts } from '../../../pure-js/libs/Texts'
import { defaultSpacing, TWELVE_PX } from '../enums/Spacings'
import { State } from '../hooks/useAppState'
import { getGrooverText } from '../libs/GrooverTextRepository'
import Box from './Box'
import GrooverText from './GrooverText'
import { SkeletonCustom } from './FigmaText'
import UserPricing from './PriceContainerUserPricing'
import { Currency, Client } from '../../../pure-js/types/GrooverTypes'
import type { FreeDropinRewardPreview } from '../hooks/useFreeDropinRewardPreview'

type Props = {
  currency: Currency
  mainUserPrices: Calculation[]
  partnerUserPrices?: Calculation[]
  hasPartner: boolean
  finalPrice: number
  vatData: Record<number, number>
  state: State
  isLoading: boolean
  client?: Client
  mainUserFreeDropinRewardPreview?: FreeDropinRewardPreview
  partnerUserFreeDropinRewardPreview?: FreeDropinRewardPreview
}

const PriceContainerComponent = ({
  currency,
  mainUserPrices,
  partnerUserPrices,
  hasPartner,
  finalPrice,
  vatData,
  state,
  isLoading,
  client,
  mainUserFreeDropinRewardPreview,
  partnerUserFreeDropinRewardPreview
}: Props) => {
  const vatLabel = getGrooverText(Texts.componentsReceiptReceiptPaymentVat)
  const vatLabelFor = (vat) => vatLabel.replace('xxx', vat)
  return (
    <Container fullPadding spacing={defaultSpacing}>
      <UserPricing
        isLoading={isLoading}
        currency={currency}
        userPrices={mainUserPrices}
        state={state}
        client={client}
        freeDropinRewardPreview={mainUserFreeDropinRewardPreview}
        userName={
          !!state.obfuscatedEnrollment?.mainUser?.name
            ? state.obfuscatedEnrollment?.mainUser?.name
            : (state.name as string)
        }
      />
      {(hasPartner || !!state.obfuscatedEnrollment?.partnerUser?.phone) && (
        <UserPricing
          isLoading={isLoading}
          currency={currency}
          userPrices={partnerUserPrices || []}
          state={state}
          client={client}
          freeDropinRewardPreview={partnerUserFreeDropinRewardPreview}
          userName={
            !!state.obfuscatedEnrollment?.partnerUser?.name
              ? state.obfuscatedEnrollment?.partnerUser?.name
              : (state.partnerName as string)
          }
        />
      )}

      {/* Show total discount */}
      {(() => {
        const totalSavings = [...mainUserPrices, ...(partnerUserPrices || [])].reduce(
          (sum, calc) =>
            sum + calc.discounts.reduce((discountSum, discount) => discountSum + (discount.discount || 0), 0) / 100,
          0
        )

        return totalSavings > 0 && [...mainUserPrices, ...(partnerUserPrices || [])].length > 2 ? (
          <TotalDiscountRow
            fullWidth
            direction="row"
            align="center"
            justify="space-between"
            fullPadding
            spacing={TWELVE_PX}
          >
            <GrooverText textKey={Texts.componentsPricingControlSavingsTotal} />
            <span>{totalSavings.toString() + ' ' + currency}</span>
          </TotalDiscountRow>
        ) : null
      })()}

      {/* Show total price */}
      <Box fullWidth direction="row" align="center" justify="space-between" top spacing="22px">
        {isLoading ? (
          <SkeletonCustom>
            <GrooverText textKey={Texts.componentsPricingControlPaymentTotalRow} />
          </SkeletonCustom>
        ) : (
          <GrooverText textKey={Texts.componentsPricingControlPaymentTotalRow} />
        )}
        {isLoading ? (
          <SkeletonCustom>
            <span>{finalPrice.toString() + ' ' + currency}</span>
          </SkeletonCustom>
        ) : (
          <span>{finalPrice.toString() + ' ' + currency}</span>
        )}
      </Box>

      {/* Show VAT */}
      {!!vatData &&
        Object.keys(vatData)
          .sort((a, b) => parseInt(a) - parseInt(b))
          .map((vat) => (
            <Box fullWidth direction="row" align="center" justify="space-between" top spacing="10px" key={vat}>
              {isLoading ? (
                <SkeletonCustom>
                  <span>{vatLabelFor(vat)}</span>
                </SkeletonCustom>
              ) : (
                <span>{vatLabelFor(vat)}</span>
              )}
              {isLoading ? (
                <SkeletonCustom>
                  <span>{vatData[vat].toString() + ' ' + currency}</span>
                </SkeletonCustom>
              ) : (
                <span>{vatData[vat].toString() + ' ' + currency}</span>
              )}
            </Box>
          ))}
    </Container>
  )
}

const TotalDiscountRow = styled(Box)`
  background-color: #f0f7eb;
  border-radius: 12px;
`

const Container = styled(Box)`
  width: 100%;
  background-color: #efefef;
  border-radius: 12px;
`

export default PriceContainerComponent
